import { Link } from 'react-router-dom';
import { useSites } from '../hooks/useSites';
import StatusBadge from '../components/StatusBadge';
import { getTemplate } from '../data/templates';
import type { SiteStatus } from '../types';
import '../styles/dashboard.css';

export default function Dashboard() {
  const { sites, loading, error } = useSites();

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>Your Sites</h1>
        </div>
        <div role="status" aria-label="Loading your sites" className="loading-state">
          <div className="spinner" aria-hidden="true" />
          <p>Loading your sites…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>Your Sites</h1>
        </div>
        <div role="alert" className="error-state">
          <p>{error}</p>
          <button className="btn btn-secondary" onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Your Sites</h1>
        <Link to="/sites/new" className="btn btn-primary">
          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Site
        </Link>
      </div>

      {sites.length === 0 ? (
        <div className="empty-state">
          <svg aria-hidden="true" width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="4" y="10" width="48" height="34" rx="4" stroke="currentColor" strokeWidth="1.8"/>
            <line x1="4" y1="20" x2="52" y2="20" stroke="currentColor" strokeWidth="1.8"/>
            <circle cx="12" cy="15" r="2" fill="currentColor"/>
            <circle cx="19" cy="15" r="2" fill="currentColor"/>
            <rect x="12" y="27" width="14" height="2.5" rx="1.25" fill="currentColor" opacity="0.5"/>
            <rect x="12" y="33" width="22" height="2" rx="1" fill="currentColor" opacity="0.3"/>
            <rect x="12" y="38" width="18" height="2" rx="1" fill="currentColor" opacity="0.3"/>
            <rect x="34" y="27" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" opacity="0.4"/>
          </svg>
          <h2>No sites yet</h2>
          <p>Create your first site to get started — it only takes a minute.</p>
          <Link to="/sites/new" className="btn btn-primary">
            Create your first site
          </Link>
        </div>
      ) : (
        <div className="sites-grid">
          {sites.map((site) => {
            const tmpl = getTemplate(site.templateId);
            return (
              <Link
                key={site.siteId}
                to={`/sites/${site.siteId}`}
                className="site-card"
                style={{ borderLeftColor: tmpl.colors.primary }}
              >
                <div className="site-card-header">
                  <h3 className="site-card-name">{site.siteName}</h3>
                  <StatusBadge status={site.status as SiteStatus} />
                </div>
                <div className="site-card-meta">
                  <span className="tp-badge">
                    <span className="tp-badge-swatches" aria-hidden="true">
                      <span className="tp-badge-swatch" style={{ background: tmpl.colors.primary }} />
                      <span className="tp-badge-swatch" style={{ background: tmpl.colors.secondary }} />
                    </span>
                    {tmpl.name}
                  </span>
                  {site.customDomain && (
                    <span className="site-card-domain">{site.customDomain}</span>
                  )}
                </div>
                <div className="site-card-date">
                  Created {new Date(site.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
